// src/pages/Marketplace/markets/OverrideAssignmentModal.tsx
import React, { useState } from 'react';
import { ErrorBanner } from '@/pages/Admin/shared';
import { useBusinessMarketOverride } from '@/pages/Marketplace/shared';
import type { Market, MarketBusiness } from '@/pages/Marketplace/shared';

interface Props {
  business: MarketBusiness;
  markets: Market[];
  apiUrl: string;
  accessToken: string | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function OverrideAssignmentModal({
  business, markets, apiUrl, accessToken, onClose, onSaved,
}: Props): React.ReactElement {
  const [value, setValue] = useState<string>(''); // empty = clear override
  const { loading, error, setOverride } = useBusinessMarketOverride(apiUrl, accessToken);

  const handleSave = async () => {
    const ok = await setOverride(business.id, value || null);
    if (ok) { onSaved(); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-white font-semibold">Override market for {business.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-xs text-gray-400">Assign to market</span>
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-2 text-sm mt-1"
            >
              <option value="">— clear override (auto-resolve) —</option>
              {markets.filter((m) => m.is_active).map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </label>
          {error && <ErrorBanner message={error} />}
        </div>
        <div className="px-6 py-4 border-t border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-gray-300 hover:text-white">Cancel</button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-700 disabled:opacity-40 rounded"
          >
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
